import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { apiEndpoints } from '../../config/api.config';
import {
  EcommerceAccountModel,
  EcommerceLoginRequestModel,
  EcommerceLoginResponseModel,
  EcommerceRegisterRequestModel,
  EcommerceRegisterResponseModel,
  EmailConfirmationRequestModel,
  MessageResponseModel,
  PasswordChangeRequestModel,
  PasswordResetConfirmRequestModel,
  PasswordResetRequestModel,
  ResendConfirmationRequestModel,
} from '../../models/ecommerce/auth.model';
import { SUPPRESS_HTTP_ERROR_TOAST } from '../../utils/http/http-context.tokens';

@Injectable({
  providedIn: 'root',
})
export class AuthRepositoryService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = apiEndpoints.auth;
  private readonly authContext = new HttpContext().set(SUPPRESS_HTTP_ERROR_TOAST, true);

  /**
   * Registrazione standalone: crea l'account ma NON apre una sessione, perché
   * l'email va confermata. Nel checkout l'account si crea invece passando
   * `account.password` a `PUT /carts/{cart_token}/customer`.
   */
  register(payload: EcommerceRegisterRequestModel): Observable<EcommerceRegisterResponseModel> {
    return this.http.post<EcommerceRegisterResponseModel>(`${this.baseUrl}/register`, payload, {
      context: this.authContext,
    });
  }

  login(payload: EcommerceLoginRequestModel): Observable<EcommerceLoginResponseModel> {
    return this.http.post<EcommerceLoginResponseModel>(`${this.baseUrl}/login`, payload, {
      context: this.authContext,
    });
  }

  me(): Observable<EcommerceAccountModel> {
    return this.http.get<EcommerceAccountModel>(`${this.baseUrl}/me`, {
      context: this.authContext,
    });
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/logout`, {}, {
      context: this.authContext,
    });
  }

  /** Conferma dell'indirizzo email tramite il token ricevuto per email. */
  confirmEmail(payload: EmailConfirmationRequestModel): Observable<MessageResponseModel> {
    return this.http.post<MessageResponseModel>(`${this.baseUrl}/confirm-email`, payload, {
      context: this.authContext,
    });
  }

  resendConfirmation(payload: ResendConfirmationRequestModel): Observable<MessageResponseModel> {
    return this.http.post<MessageResponseModel>(`${this.baseUrl}/resend-confirmation`, payload, {
      context: this.authContext,
    });
  }

  /** Reset password, passo 1: il backend invia l'email con il link e il token. */
  requestPasswordReset(payload: PasswordResetRequestModel): Observable<MessageResponseModel> {
    return this.http.post<MessageResponseModel>(`${this.baseUrl}/password-reset`, payload, {
      context: this.authContext,
    });
  }

  /** Reset password, passo 2: nuova password con il token del link. */
  confirmPasswordReset(
    payload: PasswordResetConfirmRequestModel
  ): Observable<MessageResponseModel> {
    return this.http.post<MessageResponseModel>(
      `${this.baseUrl}/password-reset/confirm`,
      payload,
      { context: this.authContext }
    );
  }

  /** Cambio password dell'utente autenticato (Bearer allegato dagli interceptor). */
  changePassword(payload: PasswordChangeRequestModel): Observable<MessageResponseModel> {
    return this.http.put<MessageResponseModel>(`${this.baseUrl}/password`, payload, {
      context: this.authContext,
    });
  }
}
